import { useState } from 'react';
import { useCircuits, DataContainer } from '@estream/sdk-browser/react';
import './Circuits.css';

type Tab = 'pending' | 'deployed' | 'completed';

export default function Circuits() {
  const [activeTab, setActiveTab] = useState<Tab>('deployed');
  const result = useCircuits();
  
  return (
    <div className="circuits-page animate-in">
      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === 'pending' ? 'active' : ''}`}
          onClick={() => setActiveTab('pending')}
        >
          Pending
          <DataContainer {...result}>
            {(c) => c.pending?.length > 0 && (
              <span className="tab-badge">{c.pending.length}</span>
            )}
          </DataContainer>
        </button>
        <button 
          className={`tab ${activeTab === 'deployed' ? 'active' : ''}`}
          onClick={() => setActiveTab('deployed')}
        >
          Deployed
        </button>
        <button 
          className={`tab ${activeTab === 'completed' ? 'active' : ''}`}
          onClick={() => setActiveTab('completed')}
        >
          Completed
        </button>
      </div>
      
      {/* Content */}
      <div className="card">
        <DataContainer {...result}>
          {(circuits) => (
            <>
              {activeTab === 'pending' && (
                <PendingCircuits circuits={circuits.pending || []} />
              )}
              {activeTab === 'deployed' && (
                <DeployedCircuits circuits={circuits.deployed || []} />
              )}
              {activeTab === 'completed' && (
                <CompletedCircuits circuits={circuits.completed || []} />
              )}
            </>
          )}
        </DataContainer>
      </div>
    </div>
  );
}

function PendingCircuits({ circuits }: { circuits: unknown[] }) {
  if (circuits.length === 0) {
    return <EmptyState message="No pending circuits" />;
  }
  
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Type</th>
          <th>ID</th>
          <th>Submitted</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {circuits.map((c, i) => {
          const circuit = c as { id?: string; proposalType?: string; type?: string };
          return (
            <tr key={i}>
              <td>
                <span className="badge badge-warning">
                  {circuit.proposalType || circuit.type || 'Unknown'}
                </span>
              </td>
              <td>
                <code>{String(circuit.id || '').slice(0, 16)}...</code>
              </td>
              <td>Just now</td>
              <td>
                <button className="btn btn-primary btn-sm">Approve</button>
                <button className="btn btn-secondary btn-sm">Reject</button>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

interface DeployedCircuit {
  circuitId: string;
  type: string;
  version: string;
  environment: string;
  deployedAt: number;
}

function DeployedCircuits({ circuits }: { circuits: DeployedCircuit[] }) {
  if (circuits.length === 0) {
    return <EmptyState message="No deployed circuits" />;
  }
  
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Circuit</th>
          <th>Version</th>
          <th>Environment</th>
          <th>Deployed</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {circuits.map((circuit) => (
          <tr key={circuit.circuitId}>
            <td>
              <span className="badge badge-success">{circuit.type}</span>
            </td>
            <td>
              <code>{circuit.version}</code>
            </td>
            <td>{circuit.environment}</td>
            <td>{formatTimeAgo(circuit.deployedAt)}</td>
            <td>
              <button className="btn btn-secondary btn-sm">Details</button>
              <button className="btn btn-danger btn-sm">Rollback</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function CompletedCircuits({ circuits }: { circuits: unknown[] }) {
  if (circuits.length === 0) {
    return <EmptyState message="No completed circuits" />;
  }
  
  return (
    <table className="table">
      <thead>
        <tr>
          <th>Type</th>
          <th>ID</th>
          <th>Result</th>
          <th>Completed</th>
        </tr>
      </thead>
      <tbody>
        {circuits.map((c, i) => {
          const circuit = c as { id?: string; type?: string; success?: boolean };
          return (
            <tr key={i}>
              <td>
                <span className="badge badge-primary">
                  {circuit.type || 'Unknown'}
                </span>
              </td>
              <td>
                <code>{String(circuit.id || '').slice(0, 16)}...</code>
              </td>
              <td>
                <span className={`badge ${circuit.success !== false ? 'badge-success' : 'badge-error'}`}>
                  {circuit.success !== false ? 'Success' : 'Failed'}
                </span>
              </td>
              <td>Recently</td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="empty-state">
      <span className="empty-icon">📦</span>
      <p>{message}</p>
    </div>
  );
}

function formatTimeAgo(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (mins > 0) return `${mins}m ago`;
  return 'Just now';
}
